import type { Types } from '../../types';
import type { ConnectedTypes } from '../../make-connected';
import { getVertexId } from '../../../core/util';
import { idOf } from './util/util';
import { makeChainable } from '../../../common/make-chainable';
import { uniqBy } from 'lodash-es';


export function fromLinkedEventsSets (
  _linkedEventsSets: ConnectedTypes.linkedEventsSet[],
  _settings:         ConnectedTypes.divisionSettings,
  presentEventIds:   Set<string>,
  options:           Types.parsedToOptions
): Types.Entities.LinkedEventsSet[] {

  return (_linkedEventsSets ?? [])
    .map(x => {
      // ensure that linkedEventsSets conform to the expected union that is currently not enforced by the schema
      if (x.type === 'parallel') {
        if (x.anchor == null) throw new Error(`Linked events set ${getVertexId(x, options)} of type "parallel" is missing "anchor" property`);
        return { ...x, type: 'parallel', anchor: x.anchor } as const;
      }
      else if (x.type === 'sequential') {
        if (x.gap == null) throw new Error(`Linked events set ${getVertexId(x, options)} of type "sequential" is missing "gap" property`);
        return { ...x, type: 'sequential', gap: x.gap } as const;
      }
      else if (x.type === 'custom') {
        if (x.configurations == null) throw new Error(`Linked events set ${getVertexId(x, options)} of type "custom" is missing "configurations" property`);
        return { ...x, type: 'custom', configurations: x.configurations } as const;
      }

      throw new Error(`Linked events set ${getVertexId(x, options)} is missing "type" property`);
    })
    .map((x): Types.Entities.LinkedEventsSet => {
      type Configurations = Types.Entities.LinkedEventsSet['configurations'];
      const id = getVertexId(x, options);

      // ensure that the events are unique with respect to their id
      const numberOfUniqueEvents = new Set(x.events.map(x => idOf.event(x, options))).size;
      if (numberOfUniqueEvents !== x.events.length) throw new Error(`Linked events set ${id} contains duplicate events after filtering out events that are not present`);

      const restrictedEvents = x.events.filter(x => presentEventIds.has(idOf.event(x, options)));
      const type = x.type;
      if (type === 'parallel') {
        const configurations: Configurations = [];
        const anchor = x.anchor;
        switch (anchor) {
          case 'start':  configurations.push(restrictedEvents.map(() => ({ anchor: 'start',  offset: 0 }))); break;
          case 'end':    configurations.push(restrictedEvents.map(() => ({ anchor: 'end',    offset: 0 }))); break;
          case 'middle': configurations.push(restrictedEvents.map(() => ({ anchor: 'middle', offset: 0 }))); break;
          default: throw new Error(`Unrecognized anchor "${anchor satisfies never as string}" in linked events set ${getVertexId(x, options)}`);
        }
        return {
          id, configurations,
          events: restrictedEvents.map(x => idOf.event(x, options))
        };
      }

      if (type === 'sequential') {
        const { min, max } = x.gap;
        const dt = _settings.discretization;

        const numConfigurations = Math.pow(1 + Math.round(max/dt - min/dt), restrictedEvents.length - 1);
        const maxNumConfigurations = 49; // max 3 events and a maximum gap range of 30 minutes => 7^2 = 49
        if (numConfigurations >= maxNumConfigurations) throw new Error(`Linked events set ${getVertexId(x, options)} has too many configurations (${numConfigurations}, max allowed is ${maxNumConfigurations})`);

        const gaps: number[] = [];
        for (let i = min; i <= max; i += dt) gaps.push(i);

        let configurations: Configurations = [[{ anchor: 'start', offset: 0 }]]; // first event is always at offset 0
        for (let i = 1; i < restrictedEvents.length; i++) {
          const newConfigurations: Configurations = [];
          for (const config of configurations) {
            const lastOffsetBottom = config[config.length - 1].offset + restrictedEvents[i - 1].preferredDuration;
            for (const gap of gaps) {
              newConfigurations.push([...config, { anchor: 'start', offset: lastOffsetBottom + gap }]);
            }
          }
          configurations = newConfigurations;
        }

        return {
          id, configurations,
          events: restrictedEvents.map(x => idOf.event(x, options))
        };
      }

      if (type === 'custom') {
        // Configurations reference the public event ids, so validate and order by `id`.
        const sortedRestrictedEvents = [...restrictedEvents]
          .sort((a, b) => {
            if (!a.id) throw new Error(`Linked events set ${id} has an event without public id in custom configuration handling`);
            if (!b.id) throw new Error(`Linked events set ${id} has an event without public id in custom configuration handling`);
            return a.id.localeCompare(b.id);
          });

        const publicIdsPresent = new Set(sortedRestrictedEvents.map(x => x.id));

        const configurations = x.configurations
          .map(_configuration => {
            // remove events that are not present and sort according to the public id to ensure a consistent order
            const configuration = _configuration
              .filter(x => publicIdsPresent.has(x.eventId))
              .sort((a, b) => a.eventId.localeCompare(b.eventId));

            // ensure that the size and order is correct
            if (configuration.length !== sortedRestrictedEvents.length) {
              throw new Error(`Linked events set ${id} has a configuration with a different number of events than the linked events set after filtering out events that are not present: expected ${sortedRestrictedEvents.length}, got ${configuration.length}`);
            }

            // ensure order (ensures no duplicates as well)
            for (let i = 0; i < sortedRestrictedEvents.length; i++) {
              if (configuration[i].eventId !== sortedRestrictedEvents[i].id) {
                throw new Error(`Linked events set ${id} has a configuration with a different order of events than the linked events set after filtering out events that are not present: expected event with id "${sortedRestrictedEvents[i].id}" at position ${i}, got event with id "${configuration[i].eventId}"`);
              }
            }

            // keep only relevant properties
            return configuration.map(({ anchor, offset }) => ({ anchor, offset }));
          });

        return { id, configurations,
          events: sortedRestrictedEvents.map(x => idOf.event(x, options))
        };
      }

      throw new Error(`Unrecognized linked events set type "${type satisfies never as string}" in linked events set ${getVertexId(x, options)}`);
    })
    .filter(x => x.events.length > 1); // filter out linked events sets that only contain one event after filtering out events that are not present
}